/**
 * THE WORDS OF THE HOME — the first day's steps and the day: agenda, tasks, what is waiting.
 *
 * English is the origin, pt-BR the translation.
 *
 * ── WHY THE STEP SENTENCES ARE HERE AND NOT IN `home-steps.ts` ────────────
 *
 * `homeSteps()` is a pure function with its own spec, and it decides WHICH
 * sentence a phase deserves — never what the sentence says. It hands back keys;
 * this file holds the words. That separation is what lets the spec keep asserting
 * the state machine ("research running says it is running") without a test
 * breaking every time somebody rewords an invitation, and it is the only way the
 * same machine can speak two languages.
 *
 * ── WHY THE COUNTERS ARE PLURALS ──────────────────────────────────────────
 *
 * They used to be two hand-written strings, `one` and `many`, chosen by
 * `count === 1`. That is vue-i18n's plural rule written by hand, and it is wrong
 * for any language whose rules are not Portuguese's. The number picks the form.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ─────────────────────────────────────────
 *
 * The table and calendar names. They are the ENTITIES this workspace built — tenant data,
 * in the tenant's own words — and translating "Pedidos" would be the panel
 * renaming a screen its owner named.
 */
export const homeMessages = {
	en: {
		title: "Get started",
		titleDone: "Today",
		subtitle: "A few short steps. At the end of them you have support running.",
		modelProblem: {
			label: "No model access.",
			none: "No model provider configured.",
			consequence: "The agents exist, but no turn will run until this is sorted out.",
		},
		redirected: {
			"modulo-restrito": "Access to this module is disabled for your account.",
			"rota-desconhecida": "That address no longer exists in this panel.",
			"so-admin": "That screen is workspace configuration and opens for administrators.",
			"so-plataforma": "That screen administers the installation, not this workspace.",
		},
		agenda: {
			title: "Schedule",
			none: "nothing today",
			new: "New in {name}",
		},
		waiting: {
			title: "Waiting for an answer",
		},
		tasks: {
			title: "My tasks",
			all: "see all",
			untitled: "(untitled)",
			nobody: "no one",
		},
		status: {
			question: "Need to check everything is up?",
			link: "See the status",
		},
		counters: {
			help: "help request | help requests",
			gaps: "note to approve | notes to approve",
		},
		steps: {
			journeyStep: "Step {step} of {total}",
			journeyBack: "Previous step",
			journeyFinish: "Finish setup",
			siteBuilding: "Your site is being built…",
			siteReady: "Your site is ready",
			progress: "{done} of {total} done",
			now: "Next step",
			allDoneTitle: "Everything is up.",
			identity: {
				title: "Confirm your business",
				body: "Save your business details and logo and personalize your assistant.",
				action: "Confirm details",
			},
			first: {
				creating: {
					title: "Shaping your business",
					body: "Shaping your business",
					action: "Open creator",
				},
				creation_ready: {
					title: "Review your identity",
					body: "Review your identity",
					action: "Open creator",
				},
				idle: {
					title: "Let Ben build your system",
					body: "Just say your business's name. He researches your company, writes the proposal — screens, fields, funnels — and shows all of it before creating anything.",
					action: "Start",
				},
				researching: {
					title: "Ben is researching your company",
					body: "The research is running right now. You can follow every step — and answer if he has a question.",
					action: "Follow the research",
				},
				dossier_ready: {
					title: "The research is done — confirm your company",
					body: "Ben wrote a dossier about your business. Check it really is your company: nothing is created before that confirmation.",
					action: "Read the dossier",
				},
				designing: {
					title: "Ben is designing the proposal",
					body: "He is reading the dossier and writing the proposal — screens, fields, funnels. Nothing is created without you seeing it first.",
					action: "Follow along",
				},
				proposal_ready: {
					title: "The proposal is ready",
					body: "See exactly what will be created and apply it whenever you want. Nothing changes without your click.",
					action: "See the proposal",
				},
				proposal_blocked: {
					title: "The proposal is blocked",
					body: "Ben stopped before fixing what the check refused. There is nothing to apply yet — continue and he picks up from the saved proposal.",
					action: "Continue",
				},
			},
			gaps: {
				title: "Confirm what the research could not find",
				body: "Ben's research left questions only the company can answer — e-mail, opening hours, policies. Nothing was thrown away: each one waits for you, and every answer passes your review before it enters the company brief.",
				action: "Answer them",
			},
			chat: {
				title: "Connect the chat to a funnel and talk as a customer",
				body: "Your site chat is already live, with your attendant answering. What is still missing is the funnel: a conversation only becomes a card on the board once the chat is connected to one. Connect it, then write as if you were a customer.",
				talkBody:
					"The chat is connected to the funnel. Now write as if you were a customer: that is how you see the attendant work, and how the first card lands on the board.",
				action: "Connect the funnel",
				talkAction: "Talk as a customer",
			},
			site: {
				title: "Create your site",
				body: "Ben builds it from what your account already knows and publishes it, while you move on.",
				action: "Create the site",
			},
			inbox: {
				title: "Take over a conversation",
				body: "When you take over, the attendant goes quiet and you are the one speaking. Handing it back is a click too — and that is what makes it answer again.",
				action: "See the conversations",
			},
		},
	},
	"pt-BR": {
		title: "Começar",
		titleDone: "Hoje",
		subtitle: "Poucos passos curtos. Ao fim deles você tem um atendimento no ar.",
		modelProblem: {
			label: "Sem acesso a modelo.",
			none: "Nenhum provedor de modelo configurado.",
			consequence: "Os agentes existem, mas nenhum turno vai rodar até isso ser resolvido.",
		},
		redirected: {
			"modulo-restrito": "O acesso a este módulo está desativado para sua conta.",
			"rota-desconhecida": "Esse endereço não existe mais neste painel.",
			"so-admin": "Essa tela é de configuração do workspace e abre para administradores.",
			"so-plataforma": "Essa tela administra a instalação, não este workspace.",
		},
		agenda: {
			title: "Agenda",
			none: "nada hoje",
			new: "Novo em {name}",
		},
		waiting: {
			title: "Esperando resposta",
		},
		tasks: {
			title: "Minhas tarefas",
			all: "ver todas",
			untitled: "(sem título)",
			nobody: "sem responsável",
		},
		status: {
			question: "Precisa conferir se tudo está de pé?",
			link: "Ver o status",
		},
		counters: {
			help: "pedido de ajuda | pedidos de ajuda",
			gaps: "anotação para aprovar | anotações para aprovar",
		},
		steps: {
			journeyStep: "Etapa {step} de {total}",
			journeyBack: "Etapa anterior",
			journeyFinish: "Concluir configuração",
			siteBuilding: "Seu site está sendo construído…",
			siteReady: "Seu site está pronto",
			progress: "{done} de {total} feitos",
			now: "Próximo passo",
			allDoneTitle: "Está tudo de pé.",
			identity: {
				title: "Confirme seu negócio",
				body: "Salve os dados e o logo do negócio e personalize seu assistente.",
				action: "Confirmar dados",
			},
			first: {
				creating: {
					title: "Dando forma ao seu negócio",
					body: "Dando forma ao seu negócio",
					action: "Abrir criador",
				},
				creation_ready: {
					title: "Revise sua identidade",
					body: "Revise sua identidade",
					action: "Abrir criador",
				},
				idle: {
					title: "Deixe o Ben montar o seu sistema",
					body: "Diga só o nome do seu negócio. Ele pesquisa a sua empresa, escreve a proposta — telas, campos, funis — e mostra tudo antes de criar qualquer coisa.",
					action: "Começar",
				},
				researching: {
					title: "O Ben está pesquisando a sua empresa",
					body: "A pesquisa está rodando agora. Você pode acompanhar cada passo — e responder se ele tiver alguma dúvida.",
					action: "Acompanhar a pesquisa",
				},
				dossier_ready: {
					title: "A pesquisa terminou — confirme a sua empresa",
					body: "O Ben escreveu um dossiê sobre o seu negócio. Confira se é a sua empresa mesmo: nada é criado antes dessa confirmação.",
					action: "Ler o dossiê",
				},
				designing: {
					title: "O Ben está desenhando a proposta",
					body: "Ele está lendo o dossiê e escrevendo a proposta — telas, campos, funis. Nada é criado sem você ver antes.",
					action: "Acompanhar",
				},
				proposal_ready: {
					title: "A proposta está pronta",
					body: "Veja exatamente o que será criado e aplique quando quiser. Nada muda sem o seu clique.",
					action: "Ver a proposta",
				},
				proposal_blocked: {
					title: "A proposta está bloqueada",
					body: "O Ben parou antes de corrigir o que a verificação recusou. Ainda não há o que aplicar — continue e ele retoma da proposta salva.",
					action: "Continuar",
				},
			},
			gaps: {
				title: "Confirme o que a pesquisa não achou",
				body: "A pesquisa do Ben deixou perguntas que só a empresa sabe responder — e-mail, horários, políticas. Nada foi descartado: cada uma espera por você, e toda resposta passa pela sua revisão antes de entrar na ficha da empresa.",
				action: "Responder",
			},
			chat: {
				title: "Conecte o chat ao funil e converse como cliente",
				body: "O chat do site já está no ar, com o seu atendente respondendo. O que falta é o funil: uma conversa só vira card no quadro quando o chat está conectado a um. Conecte, e depois escreva como se fosse um cliente.",
				talkBody:
					"O chat está conectado ao funil. Agora escreva como se fosse um cliente: é assim que se vê o atendente trabalhando — e o primeiro card cair no quadro.",
				action: "Conectar o funil",
				talkAction: "Conversar como cliente",
			},
			site: {
				title: "Crie seu site",
				body: "O Ben constrói com o que a conta já sabe e publica, enquanto você segue.",
				action: "Criar o site",
			},
			inbox: {
				title: "Assuma uma conversa",
				body: "Quando você assume, o atendente cala e quem fala é você. Devolver também é um clique — e é o que faz ele voltar a responder.",
				action: "Ver as conversas",
			},
		},
	},
	el: {
		title: "Ξεκίνημα",
		titleDone: "Σήμερα",
		subtitle: "Λίγα σύντομα βήματα. Στο τέλος τους έχεις την εξυπηρέτηση σε λειτουργία.",
		modelProblem: {
			label: "Χωρίς πρόσβαση σε μοντέλο.",
			none: "Δεν έχει ρυθμιστεί κανένας πάροχος μοντέλων.",
			consequence: "Οι πράκτορες υπάρχουν, αλλά κανένας γύρος δεν θα τρέξει μέχρι να λυθεί αυτό.",
		},
		redirected: {
			"modulo-restrito":
				"Η πρόσβαση σε αυτή την ενότητα έχει απενεργοποιηθεί για τον λογαριασμό σας.",
			"rota-desconhecida": "Αυτή η διεύθυνση δεν υπάρχει πια σε αυτό το πάνελ.",
			"so-admin": "Αυτή η οθόνη είναι ρύθμιση του χώρου εργασίας και ανοίγει για διαχειριστές.",
			"so-plataforma": "Αυτή η οθόνη διαχειρίζεται την εγκατάσταση, όχι αυτόν τον χώρο εργασίας.",
		},
		agenda: {
			title: "Πρόγραμμα",
			none: "τίποτα σήμερα",
			new: "Νέο σε {name}",
		},
		waiting: {
			title: "Περιμένουν απάντηση",
		},
		tasks: {
			title: "Οι εργασίες μου",
			all: "προβολή όλων",
			untitled: "(χωρίς τίτλο)",
			nobody: "χωρίς υπεύθυνο",
		},
		status: {
			question: "Θέλεις να δεις αν όλα λειτουργούν;",
			link: "Δες την κατάσταση",
		},
		counters: {
			help: "αίτημα βοήθειας | αιτήματα βοήθειας",
			gaps: "σημείωση προς έγκριση | σημειώσεις προς έγκριση",
		},
		steps: {
			journeyStep: "Βήμα {step} από {total}",
			journeyBack: "Προηγούμενο βήμα",
			journeyFinish: "Ολοκλήρωση ρύθμισης",
			siteBuilding: "Ο ιστότοπός σας κατασκευάζεται…",
			siteReady: "Ο ιστότοπός σας είναι έτοιμος",
			progress: "{done} από {total} ολοκληρωμένα",
			now: "Επόμενο βήμα",
			allDoneTitle: "Όλα λειτουργούν.",
			identity: {
				title: "Επιβεβαιώστε την επιχείρηση",
				body: "Αποθηκεύστε τα στοιχεία και το λογότυπο και προσαρμόστε τον βοηθό σας.",
				action: "Επιβεβαίωση",
			},
			first: {
				creating: {
					title: "Διαμορφώνουμε την επιχείρησή σας",
					body: "Διαμορφώνουμε την επιχείρησή σας",
					action: "Άνοιγμα δημιουργού",
				},
				creation_ready: {
					title: "Ελέγξτε την ταυτότητά σας",
					body: "Ελέγξτε την ταυτότητά σας",
					action: "Άνοιγμα δημιουργού",
				},
				idle: {
					title: "Άσε τον Ben να χτίσει το σύστημά σου",
					body: "Πες μόνο το όνομα της επιχείρησής σου. Ερευνά την επιχείρηση, γράφει την πρόταση — οθόνες, πεδία, χοάνες — και τα δείχνει όλα πριν δημιουργήσει οτιδήποτε.",
					action: "Έναρξη",
				},
				researching: {
					title: "Ο Ben ερευνά την επιχείρησή σου",
					body: "Η έρευνα τρέχει αυτή τη στιγμή. Μπορείς να παρακολουθήσεις κάθε βήμα — και να απαντήσεις αν έχει κάποια απορία.",
					action: "Παρακολούθηση της έρευνας",
				},
				dossier_ready: {
					title: "Η έρευνα τελείωσε — επιβεβαίωσε την επιχείρησή σου",
					body: "Ο Ben έγραψε έναν φάκελο για την επιχείρησή σου. Έλεγξε ότι είναι όντως η επιχείρησή σου: τίποτα δεν δημιουργείται πριν από αυτή την επιβεβαίωση.",
					action: "Ανάγνωση του φακέλου",
				},
				designing: {
					title: "Ο Ben σχεδιάζει την πρόταση",
					body: "Διαβάζει τον φάκελο και γράφει την πρόταση — οθόνες, πεδία, χοάνες. Τίποτα δεν δημιουργείται χωρίς να το δεις πρώτα.",
					action: "Παρακολούθηση",
				},
				proposal_ready: {
					title: "Η πρόταση είναι έτοιμη",
					body: "Δες ακριβώς τι θα δημιουργηθεί και εφάρμοσέ το όποτε θέλεις. Τίποτα δεν αλλάζει χωρίς το κλικ σου.",
					action: "Προβολή της πρότασης",
				},
				proposal_blocked: {
					title: "Η πρόταση είναι μπλοκαρισμένη",
					body: "Ο Ben σταμάτησε πριν διορθώσει ό,τι απέρριψε ο έλεγχος. Δεν υπάρχει ακόμη κάτι για εφαρμογή — συνεχίστε και συνεχίζει από την αποθηκευμένη πρόταση.",
					action: "Συνέχεια",
				},
			},
			gaps: {
				title: "Επιβεβαίωσε ό,τι δεν βρήκε η έρευνα",
				body: "Η έρευνα του Ben άφησε ερωτήσεις που μόνο η επιχείρηση μπορεί να απαντήσει — email, ωράριο, πολιτικές. Τίποτα δεν πετάχτηκε: καθεμία περιμένει εσένα, και κάθε απάντηση περνά από τον έλεγχό σου πριν μπει στην ταυτότητα της επιχείρησης.",
				action: "Απάντησε",
			},
			chat: {
				title: "Σύνδεσε το chat με μια χοάνη και μίλησε σαν πελάτης",
				body: "Το chat του site είναι ήδη σε λειτουργία, με τον εξυπηρετητή σου να απαντά. Λείπει η χοάνη: μια συνομιλία γίνεται κάρτα στον πίνακα μόνο όταν το chat είναι συνδεδεμένο με μία. Σύνδεσέ το και μετά γράψε σαν να ήσουν πελάτης.",
				talkBody:
					"Το chat είναι συνδεδεμένο με τη χοάνη. Τώρα γράψε σαν πελάτης: έτσι βλέπεις τον εξυπηρετητή να δουλεύει — και την πρώτη κάρτα να μπαίνει στον πίνακα.",
				action: "Σύνδεση χοάνης",
				talkAction: "Μίλα σαν πελάτης",
			},
			site: {
				title: "Δημιούργησε το site σου",
				body: "Ο Ben το φτιάχνει από όσα ξέρει ήδη ο λογαριασμός και το δημοσιεύει, ενώ εσείς συνεχίζετε.",
				action: "Δημιουργία του site",
			},
			inbox: {
				title: "Ανάλαβε μια συνομιλία",
				body: "Όταν αναλαμβάνεις, ο εκπρόσωπος σωπαίνει και μιλάς εσύ. Η επιστροφή είναι κι αυτή ένα κλικ — και είναι αυτό που τον κάνει να απαντήσει ξανά.",
				action: "Προβολή των συνομιλιών",
			},
		},
	},
	es: {
		title: "Empezar",
		titleDone: "Hoy",
		subtitle: "Unos pocos pasos cortos. Al final de ellos tienes la atención funcionando.",
		modelProblem: {
			label: "Sin acceso a modelo.",
			none: "Ningún proveedor de modelos configurado.",
			consequence: "Los agentes existen, pero no se ejecutará ningún turno hasta resolver esto.",
		},
		redirected: {
			"modulo-restrito": "El acceso a este módulo está deshabilitado para tu cuenta.",
			"rota-desconhecida": "Esa dirección ya no existe en este panel.",
			"so-admin": "Esa pantalla son ajustes del espacio de trabajo y se abre para administradores.",
			"so-plataforma": "Esa pantalla administra la instalación, no este espacio de trabajo.",
		},
		agenda: {
			title: "Agenda",
			none: "nada hoy",
			new: "Nuevo en {name}",
		},
		waiting: {
			title: "Esperando respuesta",
		},
		tasks: {
			title: "Mis tareas",
			all: "ver todas",
			untitled: "(sin título)",
			nobody: "sin responsable",
		},
		status: {
			question: "¿Quieres comprobar que todo está en pie?",
			link: "Ver el estado",
		},
		counters: {
			help: "solicitud de ayuda | solicitudes de ayuda",
			gaps: "nota por aprobar | notas por aprobar",
		},
		steps: {
			journeyStep: "Paso {step} de {total}",
			journeyBack: "Paso anterior",
			journeyFinish: "Finalizar configuración",
			siteBuilding: "Tu sitio se está construyendo…",
			siteReady: "Tu sitio está listo",
			progress: "{done} de {total} hechos",
			now: "Siguiente paso",
			allDoneTitle: "Está todo en pie.",
			identity: {
				title: "Confirma tu negocio",
				body: "Guarda los datos y el logo del negocio y personaliza tu asistente.",
				action: "Confirmar datos",
			},
			first: {
				creating: {
					title: "Dando forma a tu negocio",
					body: "Dando forma a tu negocio",
					action: "Abrir creador",
				},
				creation_ready: {
					title: "Revisa tu identidad",
					body: "Revisa tu identidad",
					action: "Abrir creador",
				},
				idle: {
					title: "Deja que Ben monte tu sistema",
					body: "Di solo el nombre de tu negocio. Él investiga tu empresa, escribe la propuesta — pantallas, campos, embudos — y lo enseña todo antes de crear nada.",
					action: "Empezar",
				},
				researching: {
					title: "Ben está investigando tu empresa",
					body: "La investigación está corriendo ahora mismo. Puedes seguir cada paso — y responder si tiene alguna duda.",
					action: "Seguir la investigación",
				},
				dossier_ready: {
					title: "La investigación ha terminado — confirma tu empresa",
					body: "Ben ha escrito un dosier sobre tu negocio. Comprueba que es de verdad tu empresa: no se crea nada antes de esa confirmación.",
					action: "Leer el dosier",
				},
				designing: {
					title: "Ben está diseñando la propuesta",
					body: "Está leyendo el dosier y escribiendo la propuesta — pantallas, campos, embudos. No se crea nada sin que lo veas antes.",
					action: "Seguirlo",
				},
				proposal_ready: {
					title: "La propuesta está lista",
					body: "Mira exactamente qué se va a crear y aplícalo cuando quieras. Nada cambia sin tu clic.",
					action: "Ver la propuesta",
				},
				proposal_blocked: {
					title: "La propuesta está bloqueada",
					body: "Ben se detuvo antes de corregir lo que la verificación rechazó. Todavía no hay nada que aplicar: continúa y él retoma desde la propuesta guardada.",
					action: "Continuar",
				},
			},
			gaps: {
				title: "Confirma lo que la investigación no encontró",
				body: "La investigación de Ben dejó preguntas que solo la empresa puede responder — e-mail, horarios, políticas. No se descartó nada: cada una te espera, y toda respuesta pasa por tu revisión antes de entrar en la ficha de la empresa.",
				action: "Responderlas",
			},
			chat: {
				title: "Conecta el chat a un embudo y conversa como cliente",
				body: "El chat del sitio ya está en el aire, con tu asistente respondiendo. Lo que falta es el embudo: una conversación solo se convierte en tarjeta del tablero cuando el chat está conectado a uno. Conéctalo y después escribe como si fueras un cliente.",
				talkBody:
					"El chat está conectado al embudo. Ahora escribe como si fueras un cliente: así ves al asistente trabajar, y la primera tarjeta caer en el tablero.",
				action: "Conectar el embudo",
				talkAction: "Conversar como cliente",
			},
			site: {
				title: "Crea tu web",
				body: "Ben la construye con lo que tu cuenta ya sabe y la publica, mientras sigues.",
				action: "Crear la web",
			},
			inbox: {
				title: "Asume una conversación",
				body: "Cuando asumes, el agente de atención se calla y quien habla eres tú. Devolverla también es un clic — y es lo que hace que vuelva a responder.",
				action: "Ver las conversaciones",
			},
		},
	},
	fr: {
		title: "Démarrer",
		titleDone: "Aujourd'hui",
		subtitle: "Quelques étapes courtes. À la fin, votre service client tourne.",
		modelProblem: {
			label: "Aucun accès à un modèle.",
			none: "Aucun fournisseur de modèles configuré.",
			consequence:
				"Les agents existent, mais aucun échange ne se lancera tant que ce n'est pas réglé.",
		},
		redirected: {
			"modulo-restrito": "L’accès à ce module est désactivé pour votre compte.",
			"rota-desconhecida": "Cette adresse n'existe plus dans ce panneau.",
			"so-admin":
				"Cet écran relève des paramètres de l'espace de travail et s'ouvre pour les administrateurs.",
			"so-plataforma": "Cet écran administre l'installation, pas cet espace de travail.",
		},
		agenda: {
			title: "Agenda",
			none: "rien aujourd'hui",
			new: "Nouveau dans {name}",
		},
		waiting: {
			title: "En attente de réponse",
		},
		tasks: {
			title: "Mes tâches",
			all: "tout voir",
			untitled: "(sans titre)",
			nobody: "sans responsable",
		},
		status: {
			question: "Besoin de vérifier que tout tourne ?",
			link: "Voir l'état",
		},
		counters: {
			help: "demande d'aide | demandes d'aide",
			gaps: "note à approuver | notes à approuver",
		},
		steps: {
			journeyStep: "Étape {step} sur {total}",
			journeyBack: "Étape précédente",
			journeyFinish: "Terminer la configuration",
			siteBuilding: "Votre site est en construction…",
			siteReady: "Votre site est prêt",
			progress: "{done} sur {total} faits",
			now: "Prochaine étape",
			allDoneTitle: "Tout tourne.",
			identity: {
				title: "Confirmez votre entreprise",
				body: "Enregistrez les informations et le logo et personnalisez votre assistant.",
				action: "Confirmer",
			},
			first: {
				creating: {
					title: "Votre entreprise prend forme",
					body: "Votre entreprise prend forme",
					action: "Ouvrir le créateur",
				},
				creation_ready: {
					title: "Vérifiez votre identité",
					body: "Vérifiez votre identité",
					action: "Ouvrir le créateur",
				},
				idle: {
					title: "Laissez Ben construire votre système",
					body: "Dites simplement le nom de votre entreprise. Il fait la recherche sur votre société, écrit la proposition — écrans, champs, pipelines — et montre tout avant de créer quoi que ce soit.",
					action: "Commencer",
				},
				researching: {
					title: "Ben fait la recherche sur votre entreprise",
					body: "La recherche tourne en ce moment. Vous pouvez suivre chaque étape — et répondre s'il a une question.",
					action: "Suivre la recherche",
				},
				dossier_ready: {
					title: "La recherche est terminée — confirmez votre entreprise",
					body: "Ben a écrit un dossier sur votre activité. Vérifiez que c'est bien votre entreprise : rien n'est créé avant cette confirmation.",
					action: "Lire le dossier",
				},
				designing: {
					title: "Ben conçoit la proposition",
					body: "Il lit le dossier et écrit la proposition — écrans, champs, pipelines. Rien n'est créé sans que vous le voyiez d'abord.",
					action: "Suivre",
				},
				proposal_ready: {
					title: "La proposition est prête",
					body: "Voyez exactement ce qui sera créé et appliquez-le quand vous voulez. Rien ne change sans votre clic.",
					action: "Voir la proposition",
				},
				proposal_blocked: {
					title: "La proposition est bloquée",
					body: "Ben s’est arrêté avant de corriger ce que la vérification a refusé. Il n’y a encore rien à appliquer : continuez et il reprend à partir de la proposition enregistrée.",
					action: "Continuer",
				},
			},
			gaps: {
				title: "Confirmez ce que la recherche n'a pas trouvé",
				body: "La recherche de Ben a laissé des questions que seule l'entreprise peut trancher — e-mail, horaires, politiques. Rien n'a été jeté : chacune vous attend, et chaque réponse passe par votre relecture avant d'entrer dans la fiche de l'entreprise.",
				action: "Y répondre",
			},
			chat: {
				title: "Connectez le chat à un pipeline et parlez comme un client",
				body: "Le chat du site est déjà en ligne, avec votre assistant qui répond. Il manque le pipeline : une conversation ne devient une carte du tableau que lorsque le chat lui est connecté. Connectez-le, puis écrivez comme si vous étiez un client.",
				talkBody:
					"Le chat est connecté au pipeline. Écrivez maintenant comme si vous étiez un client : c'est ainsi que vous voyez l'assistant travailler, et la première carte arriver sur le tableau.",
				action: "Connecter le pipeline",
				talkAction: "Parler comme un client",
			},
			site: {
				title: "Créez votre site",
				body: "Ben le construit avec ce que votre compte sait déjà et le publie, pendant que vous avancez.",
				action: "Créer le site",
			},
			inbox: {
				title: "Prenez en charge une conversation",
				body: "Quand vous prenez en charge, le conseiller se tait et c'est vous qui parlez. La rendre est aussi un clic — et c'est ce qui le fait répondre à nouveau.",
				action: "Voir les conversations",
			},
		},
	},
	it: {
		title: "Inizia",
		titleDone: "Oggi",
		subtitle: "Pochi passi brevi. Alla fine hai l'assistenza attiva.",
		modelProblem: {
			label: "Nessun accesso ai modelli.",
			none: "Nessun fornitore di modelli configurato.",
			consequence: "Gli agenti esistono, ma nessun turno partirà finché non si risolve questo.",
		},
		redirected: {
			"modulo-restrito": "L’accesso a questo modulo è disabilitato per il tuo account.",
			"rota-desconhecida": "Questo indirizzo non esiste più in questo pannello.",
			"so-admin":
				"Quella schermata riguarda le impostazioni dello spazio di lavoro e si apre per gli amministratori.",
			"so-plataforma": "Quella schermata amministra l'installazione, non questo spazio di lavoro.",
		},
		agenda: {
			title: "Agenda",
			none: "niente oggi",
			new: "Nuovo in {name}",
		},
		waiting: {
			title: "In attesa di risposta",
		},
		tasks: {
			title: "Le mie attività",
			all: "vedi tutte",
			untitled: "(senza titolo)",
			nobody: "senza responsabile",
		},
		status: {
			question: "Vuoi controllare che sia tutto attivo?",
			link: "Vedi lo stato",
		},
		counters: {
			help: "richiesta di aiuto | richieste di aiuto",
			gaps: "nota da approvare | note da approvare",
		},
		steps: {
			journeyStep: "Passaggio {step} di {total}",
			journeyBack: "Passaggio precedente",
			journeyFinish: "Completa configurazione",
			siteBuilding: "Il tuo sito è in costruzione…",
			siteReady: "Il tuo sito è pronto",
			progress: "{done} di {total} fatti",
			now: "Prossimo passo",
			allDoneTitle: "È tutto attivo.",
			identity: {
				title: "Conferma la tua attività",
				body: "Salva i dati e il logo e personalizza il tuo assistente.",
				action: "Conferma dati",
			},
			first: {
				creating: {
					title: "La tua attività prende forma",
					body: "La tua attività prende forma",
					action: "Apri il creatore",
				},
				creation_ready: {
					title: "Rivedi la tua identità",
					body: "Rivedi la tua identità",
					action: "Apri il creatore",
				},
				idle: {
					title: "Lascia che Ben costruisca il tuo sistema",
					body: "Di' solo il nome della tua attività. Lui fa ricerca sulla tua azienda, scrive la proposta — schermate, campi, pipeline — e mostra tutto prima di creare qualsiasi cosa.",
					action: "Inizia",
				},
				researching: {
					title: "Ben sta facendo ricerca sulla tua azienda",
					body: "La ricerca sta girando proprio ora. Puoi seguire ogni passo — e rispondere se ha una domanda.",
					action: "Segui la ricerca",
				},
				dossier_ready: {
					title: "La ricerca è finita — conferma la tua azienda",
					body: "Ben ha scritto un dossier sulla tua attività. Controlla che sia davvero la tua azienda: niente viene creato prima di questa conferma.",
					action: "Leggi il dossier",
				},
				designing: {
					title: "Ben sta progettando la proposta",
					body: "Sta leggendo il dossier e scrivendo la proposta — schermate, campi, pipeline. Niente viene creato senza che tu lo veda prima.",
					action: "Segui",
				},
				proposal_ready: {
					title: "La proposta è pronta",
					body: "Vedi esattamente cosa verrà creato e applicalo quando vuoi. Niente cambia senza il tuo clic.",
					action: "Vedi la proposta",
				},
				proposal_blocked: {
					title: "La proposta è bloccata",
					body: "Ben si è fermato prima di correggere ciò che la verifica ha rifiutato. Non c’è ancora nulla da applicare: continua e lui riprende dalla proposta salvata.",
					action: "Continua",
				},
			},
			gaps: {
				title: "Conferma quello che la ricerca non ha trovato",
				body: "La ricerca di Ben ha lasciato domande a cui solo l'azienda può rispondere — e-mail, orari, politiche. Niente è stato buttato: ognuna aspetta te, e ogni risposta passa dalla tua revisione prima di entrare nel profilo dell'azienda.",
				action: "Rispondi",
			},
			chat: {
				title: "Collega la chat a una pipeline e parla come un cliente",
				body: "La chat del sito è già attiva, con il tuo assistente che risponde. Manca la pipeline: una conversazione diventa una scheda della bacheca solo quando la chat è collegata a una. Collegala, poi scrivi come se fossi un cliente.",
				talkBody:
					"La chat è collegata alla pipeline. Ora scrivi come se fossi un cliente: è così che vedi l'assistente lavorare, e la prima scheda arrivare in bacheca.",
				action: "Collegare la pipeline",
				talkAction: "Parlare come un cliente",
			},
			site: {
				title: "Crea il tuo sito",
				body: "Ben lo costruisce con ciò che il tuo account sa già e lo pubblica, mentre vai avanti.",
				action: "Crea il sito",
			},
			inbox: {
				title: "Prendi in carico una conversazione",
				body: "Quando prendi in carico, l'operatore tace e a parlare sei tu. Anche restituirla è un clic — ed è quello che lo fa tornare a rispondere.",
				action: "Vedi le conversazioni",
			},
		},
	},
	de: {
		title: "Loslegen",
		titleDone: "Heute",
		subtitle: "Ein paar kurze Schritte. Am Ende davon läuft Ihr Kundenservice.",
		modelProblem: {
			label: "Kein Modellzugang.",
			none: "Kein Modellanbieter eingerichtet.",
			consequence:
				"Die Agenten sind da, aber kein Durchlauf startet, solange das nicht geklärt ist.",
		},
		redirected: {
			"modulo-restrito": "Der Zugriff auf dieses Modul ist für Ihr Konto deaktiviert.",
			"rota-desconhecida": "Diese Adresse gibt es in diesem Panel nicht mehr.",
			"so-admin":
				"Dieser Bereich gehört zu den Einstellungen des Workspace und öffnet sich für Administratoren.",
			"so-plataforma": "Dieser Bereich verwaltet die Installation, nicht diesen Workspace.",
		},
		agenda: {
			title: "Termine",
			none: "heute nichts",
			new: "Neu in {name}",
		},
		waiting: {
			title: "Warten auf Antwort",
		},
		tasks: {
			title: "Meine Aufgaben",
			all: "alle ansehen",
			untitled: "(ohne Titel)",
			nobody: "niemand zugewiesen",
		},
		status: {
			question: "Wollen Sie prüfen, ob alles läuft?",
			link: "Den Status ansehen",
		},
		counters: {
			help: "Hilfeanfrage | Hilfeanfragen",
			gaps: "Notiz zur Freigabe | Notizen zur Freigabe",
		},
		steps: {
			journeyStep: "Schritt {step} von {total}",
			journeyBack: "Vorheriger Schritt",
			journeyFinish: "Einrichtung abschließen",
			siteBuilding: "Ihre Website wird gebaut…",
			siteReady: "Ihre Website ist fertig",
			progress: "{done} von {total} erledigt",
			now: "Nächster Schritt",
			allDoneTitle: "Alles läuft.",
			identity: {
				title: "Unternehmen bestätigen",
				body: "Speichern Sie Unternehmensdaten und Logo und passen Sie Ihren Assistenten an.",
				action: "Daten bestätigen",
			},
			first: {
				creating: {
					title: "Ihr Unternehmen nimmt Gestalt an",
					body: "Ihr Unternehmen nimmt Gestalt an",
					action: "Ersteller öffnen",
				},
				creation_ready: {
					title: "Prüfen Sie Ihre Identität",
					body: "Prüfen Sie Ihre Identität",
					action: "Ersteller öffnen",
				},
				idle: {
					title: "Lassen Sie Ben Ihr System bauen",
					body: "Sagen Sie einfach den Namen Ihres Unternehmens. Er recherchiert Ihre Firma, schreibt den Vorschlag — Masken, Felder, Pipelines — und zeigt alles, bevor er irgendetwas anlegt.",
					action: "Starten",
				},
				researching: {
					title: "Ben recherchiert Ihre Firma",
					body: "Die Recherche läuft gerade. Sie können jeden Schritt mitverfolgen — und antworten, wenn er eine Frage hat.",
					action: "Der Recherche folgen",
				},
				dossier_ready: {
					title: "Die Recherche ist fertig — bestätigen Sie Ihre Firma",
					body: "Ben hat ein Dossier über Ihr Unternehmen geschrieben. Prüfen Sie, ob es wirklich Ihre Firma ist: Vor dieser Bestätigung wird nichts angelegt.",
					action: "Das Dossier lesen",
				},
				designing: {
					title: "Ben entwirft den Vorschlag",
					body: "Er liest das Dossier und schreibt den Vorschlag — Masken, Felder, Pipelines. Nichts wird angelegt, ohne dass Sie es vorher sehen.",
					action: "Mitverfolgen",
				},
				proposal_ready: {
					title: "Der Vorschlag ist fertig",
					body: "Sehen Sie genau, was angelegt wird, und wenden Sie es an, wann Sie wollen. Nichts ändert sich ohne Ihren Klick.",
					action: "Den Vorschlag ansehen",
				},
				proposal_blocked: {
					title: "Der Vorschlag ist blockiert",
					body: "Ben hat angehalten, bevor er korrigiert hat, was die Prüfung abgelehnt hat. Es gibt noch nichts anzuwenden – fahren Sie fort, und er macht beim gespeicherten Vorschlag weiter.",
					action: "Fortfahren",
				},
			},
			gaps: {
				title: "Bestätigen Sie, was die Recherche nicht gefunden hat",
				body: "Bens Recherche hat Fragen hinterlassen, die nur das Unternehmen beantworten kann — E-Mail, Öffnungszeiten, Richtlinien. Nichts wurde weggeworfen: Jede wartet auf Sie, und jede Antwort geht durch Ihre Prüfung, bevor sie ins Unternehmensprofil kommt.",
				action: "Beantworten",
			},
			chat: {
				title: "Verbinde den Chat mit einer Pipeline und schreib wie ein Kunde",
				body: "Der Site-Chat ist schon live, und dein Assistent antwortet. Was fehlt, ist die Pipeline: ein Gespräch wird erst dann zur Karte auf dem Board, wenn der Chat mit einer verbunden ist. Verbinde ihn und schreib dann, als wärst du ein Kunde.",
				talkBody:
					"Der Chat ist mit der Pipeline verbunden. Schreib jetzt, als wärst du ein Kunde: so siehst du den Assistenten arbeiten — und die erste Karte auf dem Board landen.",
				action: "Pipeline verbinden",
				talkAction: "Wie ein Kunde schreiben",
			},
			site: {
				title: "Erstellen Sie Ihre Website",
				body: "Ben baut sie aus dem, was Ihr Konto schon weiß, und veröffentlicht sie, während Sie weitermachen.",
				action: "Die Website erstellen",
			},
			inbox: {
				title: "Übernehmen Sie eine Konversation",
				body: "Wenn Sie übernehmen, verstummt der Kundenbetreuer und Sie sind es, der spricht. Zurückgeben ist auch nur ein Klick — und genau das lässt ihn wieder antworten.",
				action: "Die Konversationen ansehen",
			},
		},
	},
} as const;
